import { useTranslation } from 'react-i18next';
import { useSelector } from '@legendapp/state/react';
import {
  ChevronDown, FileText, Highlighter, MessageSquare, Cpu, SlidersHorizontal,
} from 'lucide-react';
import ComposerSettingsItems from './ComposerSettingsItems';
import { settings$ } from '../../store/settings$';
import { useRAGChat } from '../../context/RAGChatContext';
import {
  ICON_SIZE, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuRadioGroup, DropdownMenuRadioItem,
  DropdownMenuLabel,
} from '../../design-system';

/**
 * Source-scope filter plus the developer-only embedding picker, shared by the
 * library and deck panels. Menu behavior and selection indicators come from RDS.
 *
 * Renders bare chips: `RAGInputArea` owns the `.input-area-filters` row so it
 * can seat mode and model switching alongside these.
 */
export function RAGInputFilters({ compact = false, mergeSettings = false }) {
  const { t } = useTranslation('reader');
  const developerMode = useSelector(() => settings$.developerMode.get());

  const {
    scopeDisplayName,
    searchSources,
    changeSearchSources,
    embeddingModel,
    changeEmbeddingModel,
    availableEmbeddingModels,
    selectedModel,
    chatThinkingEnabled,
    updateChatThinking,
    webSearchEnabled,
    updateWebSearch,
    streamingEnabled,
    setStreamingEnabled,
  } = useRAGChat() || {};

  const currentEmbeddingModel =
    availableEmbeddingModels?.find((m) => m.id === embeddingModel) || availableEmbeddingModels?.[0];

  const getSourcesLabel = () => {
    if (!searchSources) return t('ragChat.sources.allSources');
    const { articles, highlights, comments } = searchSources;
    const enabledCount = [articles, highlights, comments].filter(Boolean).length;
    if (enabledCount === 3) return t('ragChat.sources.allSources');
    if (enabledCount === 0) return t('ragChat.sources.noSources');
    const parts = [];
    if (articles) parts.push(t('ragChat.sources.articles'));
    if (highlights) parts.push(t('ragChat.sources.highlights'));
    if (comments) parts.push(t('ragChat.sources.comments'));
    return parts.join(' + ');
  };

  const toggleSource = (source) => {
    if (!searchSources || !changeSearchSources) return;
    const newSources = { ...searchSources, [source]: !searchSources[source] };
    // Searching nothing would return nothing — keep at least one source on.
    if (Object.values(newSources).filter(Boolean).length === 0) return;
    changeSearchSources(newSources);
  };

  const SOURCE_OPTIONS = [
    { key: 'articles', icon: FileText, label: t('ragChat.sources.articles') },
    { key: 'highlights', icon: Highlighter, label: t('ragChat.sources.highlights') },
    { key: 'comments', icon: MessageSquare, label: t('ragChat.sources.comments') },
  ];
  const sourcesButtonLabel = `${t('ragChat.filterSources')}: ${getSourcesLabel()}`;

  return (
    <>
      <div className="source-filter-selector input-area-filter">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`source-filter-btn${compact ? ' input-area-icon-filter' : ''}`}
              aria-label={sourcesButtonLabel}
              title={sourcesButtonLabel}
            >
              <SlidersHorizontal size={compact ? ICON_SIZE.xs : ICON_SIZE.xxs} aria-hidden="true" />
              {!compact && (
                <>
                  <span>{getSourcesLabel()}</span>
                  <ChevronDown size={ICON_SIZE.xxs} aria-hidden="true" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={`source-filter-dropdown${mergeSettings ? ' model-panel-menu' : ''}`} side="top" align="start">
            <DropdownMenuLabel>
              {t('ragChat.searchIn', { scope: scopeDisplayName })}
            </DropdownMenuLabel>
            {SOURCE_OPTIONS.map((option) => {
              const Icon = option.icon;
              const checked = Boolean(searchSources?.[option.key]);
              const isLastSource = checked && Object.values(searchSources).filter(Boolean).length === 1;
              return (
                <DropdownMenuCheckboxItem
                  key={option.key}
                  checked={checked}
                  disabled={isLastSource}
                  onCheckedChange={() => toggleSource(option.key)}
                  onSelect={(event) => event.preventDefault()}
                >
                  <Icon size={ICON_SIZE.xs} aria-hidden="true" />
                  <span>{option.label}</span>
                </DropdownMenuCheckboxItem>
              );
            })}
            {mergeSettings && (
              <ComposerSettingsItems
                selectedModel={selectedModel}
                thinkingEnabled={chatThinkingEnabled}
                setThinkingEnabled={updateChatThinking}
                webSearchEnabled={webSearchEnabled}
                setWebSearchEnabled={updateWebSearch}
                streamingEnabled={streamingEnabled}
                setStreamingEnabled={setStreamingEnabled}
              />
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {developerMode && (
        <div className="embedding-model-selector input-area-filter">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="embedding-model-btn" title={t('ragChat.embeddingModelHint')}>
                <Cpu size={ICON_SIZE.xxs} aria-hidden="true" />
                <span>{currentEmbeddingModel?.name || 'Qwen3-8B'}</span>
                <ChevronDown size={ICON_SIZE.xxs} aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="embedding-model-dropdown" side="top" align="start">
              <DropdownMenuLabel>{t('ragChat.embeddingModel')}</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={embeddingModel} onValueChange={changeEmbeddingModel}>
                {availableEmbeddingModels?.map((model) => (
                  <DropdownMenuRadioItem key={model.id} value={model.id} textValue={`${model.name} (${model.provider})`}>
                    <span>{model.name} ({model.provider})</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </>
  );
}

export default RAGInputFilters;
